import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaBackward, FaSearch } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const toInputDate = (d) => d.toISOString().split("T")[0];

const HsdHrUpdateAdd = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  const BASE = "http://nissi-india.com/api/hsdhr";

  // ─────────────────────────────────────────────────────────────
  // LocalStorage values
  // ─────────────────────────────────────────────────────────────
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const branch =
    localStorage.getItem("branchType") || localStorage.getItem("branch") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": storedBranchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, storedBranchId, branch, userId]
  );

  // Default dates: from = today − 1 month, to = today
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return toInputDate(d);
  });

  const [toDate, setToDate] = useState(() => toInputDate(new Date()));

  // Branch/vehicle state
  const [selectedBranchId, setSelectedBranchId] = useState(() => {
    return storedBranchId ? parseInt(storedBranchId, 10) : 0;
  });

  const [vehicleCode, setVehicleCode] = useState("");
  const [branchList, setBranchList] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);

  // Raw grid data from API
  const [rawData, setRawData] = useState([]);

  // Normalize rows to the EXACT field names your UI uses
  const data = useMemo(() => {
    return (rawData || []).map((r) => {
      const preStartHr = r.fld_Pre_HSDStartingHr ?? r.fld_preStartingHr ?? 0;
      const curStartHr = r.fld_Cur_HSDStartingHr ?? r.fld_curStartingHr ?? 0;
      const preCloseHr = r.fld_Pre_HSDClosingHr ?? r.fld_preClosingHr ?? 0;
      const curCloseHr = r.fld_Cur_HSDClosingHr ?? r.fld_curClosingHr ?? 0;

      const preStartKm = r.fld_Pre_HSDStartingKM ?? r.fld_preStartingKM ?? 0;
      const curStartKm = r.fld_Cur_HSDStartingKM ?? r.fld_curStartingKM ?? 0;
      const preCloseKm = r.fld_Pre_HSDClosingKM ?? r.fld_preClosingKM ?? 0;
      const curCloseKm = r.fld_Cur_HSDClosingKM ?? r.fld_curClosingKM ?? 0;

      const sidetId =
        r.fld_storesissuedetId ?? r.fld_storesissueDetid ?? r.SIdetId ?? null;

      const date =
        r.fld_date ??
        r.fld_SIDate ??
        r.fld_siDate ??
        r.fld_Date ??
        "";

      const qty = r.fld_qty ?? r.fld_Qty ?? null;

      const vehicleDisplay =
        (r.fld_vehiclecodename &&
        String(r.fld_vehiclecodename).trim() !== ""
          ? r.fld_vehiclecodename
          : null) ||
        (r.fld_vehiclecode &&
        String(r.fld_vehiclecode).trim() !== "" &&
        r.fld_vehiclecode !== " : "
          ? r.fld_vehiclecode
          : null) ||
        (r.fld_vehicleCode ? r.fld_vehicleCode : null) ||
        "—";

      return {
        ...r,

        fld_storesissuedetId: sidetId,
        fld_branchid: r.fld_branchid ?? r.fld_branchId ?? selectedBranchId,
        fld_itemid: r.fld_itemid ?? r.fld_itemId ?? 0,

        fld_date: date,
        fld_qty: qty,

        fld_vehiclecodename: vehicleDisplay,
        fld_vehiclecode: vehicleDisplay,

        fld_Pre_HSDStartingHr: preStartHr,
        fld_Cur_HSDStartingHr: curStartHr,
        fld_Pre_HSDClosingHr: preCloseHr,
        fld_Cur_HSDClosingHr: curCloseHr,

        fld_Pre_HSDStartingKM: preStartKm,
        fld_Cur_HSDStartingKM: curStartKm,
        fld_Pre_HSDClosingKM: preCloseKm,
        fld_Cur_HSDClosingKM: curCloseKm,

        fld_username: r.fld_username ?? r.fld_userName ?? "",
      };
    });
  }, [rawData, selectedBranchId]);

  // Per-row editable values + checkbox state
  const [editedRows, setEditedRows] = useState({});
  const [checkedRows, setCheckedRows] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load dropdowns
  useEffect(() => {
    if (!selectedBranchId) return;

    axios
      .get(`${BASE}/hsd_dropdown?branchId=${selectedBranchId}`, {
        headers: commonHeaders,
      })
      .then((res) => {
        const vehicles = res.data.vehicleCodes || [];
        const branches = res.data.branches || [];
        setVehicleOptions(
          vehicles.filter(
            (v) => v.fld_vehichlecode && v.fld_vehichlecode !== "Select"
          )
        );
        setBranchList(
          branches.filter(
            (b) => b.fld_Branchid !== "" && b.fld_Branchid != null
          )
        );
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load dropdown data");
      });
  }, [selectedBranchId, commonHeaders]);

  // Auto load grid on mount
  useEffect(() => {
    fetchGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch grid
  const fetchGrid = () => {
    if (!fromDate || !toDate) {
      toast.warning("Please Select From Date and To Date");
      return;
    }

    const queryParams = new URLSearchParams({
      branchId: selectedBranchId || 0,
      fromDate,
      toDate,
      vehicleCode: vehicleCode || "%",
    });

    axios
      .get(`${BASE}/hsd_entry_list?${queryParams.toString()}`, {
        headers: commonHeaders,
      })
      .then((res) => {
        const records = res.data?.data || [];
        setRawData(records);
        if (records.length === 0) toast.info("No records found.");
      })
      .catch((err) => {
        console.error("HSD entry list fetch failed", err);
        toast.error("Failed to fetch HSD records");
      });
  };

  // IMPORTANT: whenever normalized data changes, initialize edits + checks
  useEffect(() => {
    const initEdits = {};
    const initChecks = {};
    data.forEach((row, idx) => {
      initEdits[idx] = {
        curStartHr: row.fld_Cur_HSDStartingHr ?? "",
        curCloseHr: row.fld_Cur_HSDClosingHr ?? "",
        curStartKm: row.fld_Cur_HSDStartingKM ?? "",
        curCloseKm: row.fld_Cur_HSDClosingKM ?? "",
      };
      initChecks[idx] = false;
    });
    setEditedRows(initEdits);
    setCheckedRows(initChecks);
    setSelectAll(false);
  }, [data]);

  const handleSearch = () => fetchGrid();

  const handleEditChange = (idx, field, value) => {
    setEditedRows((prev) => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value },
    }));
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    const updated = {};
    data.forEach((_, idx) => {
      updated[idx] = checked;
    });
    setCheckedRows(updated);
  };

  const handleRowCheck = (idx, checked) => {
    setCheckedRows((prev) => ({ ...prev, [idx]: checked }));
    if (!checked) setSelectAll(false);
  };

  const handleSubmit = () => {
    const selectedEntries = data
      .map((row, idx) => ({ row, idx }))
      .filter(({ idx }) => checkedRows[idx])
      .map(({ row, idx }) => ({
        SIdetId: parseInt(row.fld_storesissuedetId, 10),
        branchId: parseInt(row.fld_branchid, 10) || 0,
        date: row.fld_date,
        vehicleCode:
          (row.fld_vehiclecode || "").split(" : ")[0].trim() ||
          row.fld_vehiclecode,
        itemId: parseInt(row.fld_itemid, 10) || 0,

        preStartHr: row.fld_Pre_HSDStartingHr ?? 0,
        curStartHr: editedRows[idx]?.curStartHr ?? 0,
        preCloseHr: row.fld_Pre_HSDClosingHr ?? 0,
        curCloseHr: editedRows[idx]?.curCloseHr ?? 0,

        preStartKm: row.fld_Pre_HSDStartingKM ?? 0,
        curStartKm: editedRows[idx]?.curStartKm ?? 0,
        preCloseKm: row.fld_Pre_HSDClosingKM ?? 0,
        curCloseKm: editedRows[idx]?.curCloseKm ?? 0,
      }));

    if (selectedEntries.length === 0) {
      toast.warning("Select at least one record.");
      return;
    }

    setIsSubmitting(true);
    axios
      .post(
        `${BASE}/vehicleHsdHourUpdate`,
        {
          userId: parseInt(userId, 10) || 0,
          entries: selectedEntries,
        },
        {
          headers: commonHeaders,
        }
      )
      .then((res) => {
        if (res.data?.success) {
          toast.success("Updated Successfully!");
          setTimeout(() => navigate("/inventory/VehicleHsdHourUpdationListing"), 1200);
        } else {
          toast.error(res.data?.message || "Update failed.");
        }
      })
      .catch((err) => {
        console.error("Submit failed", err);
        toast.error("Failed to save records.");
      })
      .finally(() => setIsSubmitting(false));
  };

  const editInputClass = `w-24 px-2 py-1 border rounded text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none ${
    isDark
      ? "bg-gray-700 border-gray-500 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const tdClass = `px-3 py-3 whitespace-nowrap text-sm ${
    isDark ? "text-gray-300" : "text-gray-800"
  }`;

  const tdReadOnly = `px-3 py-3 whitespace-nowrap text-sm font-medium ${
    isDark ? "text-blue-300" : "text-blue-700"
  }`;

  return (
    <LayoutWrapper>
      <HeaderBar title="HSD HOUR UPDATION ENTRY" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              HSD Hour Updation Entry
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {data.length} Records
            </span>
          </div>

          <button
            onClick={() => navigate("/inventory/VehicleHsdHourUpdationListing")}
            className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
          >
            <FaBackward /> Back
          </button>
        </div>

        <div className={`rounded-xl p-6 mb-6 border ${isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"}`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}>
            Search Filters
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                From Date *
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                To Date *
              </label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Vehicle Code
              </label>
              <select
                value={vehicleCode}
                onChange={(e) => setVehicleCode(e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value="">-- Select Vehicle --</option>
                {vehicleOptions.map((veh, idx) => (
                  <option key={idx} value={veh.fld_vehichlecode}>
                    {veh.fld_vehichlecode}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Branch
              </label>
              <select
                value={selectedBranchId}
                onChange={(e) => setSelectedBranchId(parseInt(e.target.value, 10) || 0)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                <option value={0}>-- Select Branch --</option>
                {branchList.map((b, idx) => (
                  <option key={idx} value={b.fld_Branchid}>
                    {b.fld_BranchName}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={`flex justify-end items-center mt-6 pt-4 border-t ${isDark ? "border-gray-600" : "border-gray-200"}`}>
            <button
              onClick={handleSearch}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
            >
              <FaSearch className="w-4 h-4" />
              Search Records
            </button>
          </div>
        </div>

        {data.length > 0 ? (
          <div className={`rounded-xl border overflow-hidden ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
            <div className={`p-4 border-b ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                HSD Hour Updation Entry ({data.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">S.No</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Issue Qty</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Vehicle Code : Name</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Pre Starting Hr</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-yellow-200 uppercase whitespace-nowrap">Cur Starting Hr ✎</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Pre Closing Hr</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-yellow-200 uppercase whitespace-nowrap">Cur Closing Hr ✎</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Pre Starting KM</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-yellow-200 uppercase whitespace-nowrap">Cur Starting KM ✎</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">Pre Closing KM</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-yellow-200 uppercase whitespace-nowrap">Cur Closing KM ✎</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-white uppercase whitespace-nowrap">User Name</th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-white uppercase whitespace-nowrap">
                      <div className="flex flex-col items-center gap-1">
                        <input
                          type="checkbox"
                          checked={selectAll}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                          className="w-4 h-4 cursor-pointer accent-white"
                          title="Select All"
                        />
                        <span className="text-xs">Select All</span>
                      </div>
                    </th>
                  </tr>
                </thead>

                <tbody className={`divide-y ${isDark ? "divide-gray-700 bg-gray-800" : "divide-gray-200 bg-white"}`}>
                  {data.map((item, idx) => (
                    <tr
                      key={item.fld_storesissuedetId ?? idx}
                      className={`transition-colors ${
                        checkedRows[idx]
                          ? isDark
                            ? "bg-blue-900/30"
                            : "bg-blue-50"
                          : idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-white hover:bg-gray-50"
                          : isDark
                          ? "bg-gray-750 hover:bg-gray-700"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <td className={tdClass}>{idx + 1}</td>
                      <td className={tdClass}>{item.fld_date || "—"}</td>
                      <td className={tdClass}>{item.fld_qty ?? "—"}</td>
                      <td className={`${tdClass} font-semibold`}>{item.fld_vehiclecodename || "—"}</td>

                      <td className={tdReadOnly}>{item.fld_Pre_HSDStartingHr ?? "—"}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editedRows[idx]?.curStartHr ?? ""}
                          onChange={(e) => handleEditChange(idx, "curStartHr", e.target.value)}
                          className={editInputClass}
                          placeholder="Enter"
                        />
                      </td>

                      <td className={tdReadOnly}>{item.fld_Pre_HSDClosingHr ?? "—"}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editedRows[idx]?.curCloseHr ?? ""}
                          onChange={(e) => handleEditChange(idx, "curCloseHr", e.target.value)}
                          className={editInputClass}
                          placeholder="Enter"
                        />
                      </td>

                      <td className={tdReadOnly}>{item.fld_Pre_HSDStartingKM ?? "—"}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editedRows[idx]?.curStartKm ?? ""}
                          onChange={(e) => handleEditChange(idx, "curStartKm", e.target.value)}
                          className={editInputClass}
                          placeholder="Enter"
                        />
                      </td>

                      <td className={tdReadOnly}>{item.fld_Pre_HSDClosingKM ?? "—"}</td>
                      <td className="px-3 py-2">
                        <input
                          type="number"
                          value={editedRows[idx]?.curCloseKm ?? ""}
                          onChange={(e) => handleEditChange(idx, "curCloseKm", e.target.value)}
                          className={editInputClass}
                          placeholder="Enter"
                        />
                      </td>

                      <td className={tdClass}>{item.fld_username || "—"}</td>

                      <td className="px-3 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={checkedRows[idx] || false}
                          onChange={(e) => handleRowCheck(idx, e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className={isDark ? "bg-gray-900" : "bg-gray-100"}>
                    <td
                      colSpan={14}
                      className={`px-4 py-3 text-right border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}
                    >
                      <button
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className={`ml-auto ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded transition-colors disabled:opacity-60 disabled:cursor-not-allowed`}
                      >
                        {isSubmitting ? "Saving..." : "Submit Selected"}
                      </button>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        ) : (
          <div className={`text-center py-12 border rounded-xl ${isDark ? "border-gray-700 text-gray-400 bg-gray-800" : "border-gray-200 text-gray-500 bg-gray-50"}`}>
            <p className="text-sm">
              No Records Found. Use the filters above to search for HSD records.
            </p>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default HsdHrUpdateAdd;